<template>
  <div class="forum-wrapper">
    <div class="forum-layout">
      <!-- Main Column -->
      <div class="main-column">
        <!-- Forum Section Header -->
        <header class="section-header">
          <h1 class="typo-headline-4">{{ section.title }}</h1>
          <p class="section-description typo-body-1">{{ section.description }}</p>
        </header>

        <!-- Threads Section -->
        <div class="threads-header">
          <h2 class="typo-headline-6">Hilos</h2>
          <button class="new-thread-btn typo-button" v-if="canWrite" @click="createNewThread">
            <i class="material-icons">add</i>
            Nuevo Hilo
          </button>
        </div>

        <!-- Loading and Error States -->
        <div v-if="loading" class="loading-message typo-body-2">
          Cargando hilos...
        </div>
        <div v-else-if="error" class="error-message typo-body-2">
          Error al cargar los hilos.
        </div>

        <!-- Threads List -->
        <div v-else class="threads-container">
          <div v-if="threads.length === 0" class="empty-threads-message typo-body-1">
            No hay hilos en esta sección. 
            <span v-if="isLoggedIn">¡Sé el primero en crear uno!</span>
          </div>
          <div v-if="threads.length > 0" v-for="thread in threads" :key="thread.id" class="thread-item" @click="goToThread(thread)">
            <div class="thread-main">
              <h3 class="thread-title typo-subtitle-1">{{ thread.title }}</h3>
              <div class="thread-meta typo-caption">
                <span class="thread-author">
                  <i class="material-icons">person</i>
                  {{ thread.author }}
                </span>
                <span class="thread-date">
                  Fecha de último envío: {{ formatDate(thread.lastUpdate) }}
                </span>
              </div>
            </div>
            <div class="thread-stats" @click.stop>
              <div class="thread-comments">
                <i class="material-icons">comment</i>
                <span>{{ thread.commentCount }}</span>
              </div>
              <router-link
                :to="{ name: 'ForumThread', params: { nid: thread.id || thread.nid }, query: { last: 'true' } }"
                class="last-message-btn typo-caption"
                target="_blank"
                @click.stop
              >
                Último mensaje
              </router-link>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="forum-pagination" v-if="totalPages > 1">
          <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn typo-button">
            Anterior
          </button>
          <span class="page-info typo-body-2">
            Página {{ currentPage }} de {{ totalPages }}
          </span>
          <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn typo-button">
            Siguiente
          </button>
        </div>
      </div>

      <!-- Side Column -->
      <div class="side-column">
        <!-- Navigation Links -->
        <navigation-links 
          v-if="section && section.id"
          :section-id="section.id"
          :section-name="section.title"
          :section-slug="$route.params.slug"
        />

        <!-- User Activity Widget -->
        <user-activity-widget />

        <!-- Recent Content -->
        <recent-content-container />

        <!-- Trending Topics -->
        <!--<trending-topics-container />-->
        
        <!-- Related Forums -->
        <!-- <related-forums-container 
          v-if="section && section.id"
          :current-section-id="section.id"
        /> -->

        <!-- Forum Stats -->
        <!--<forum-stats-container />-->
      </div>
    </div>
  </div>
</template>

<script>
import forumApi from '@/features/forum/services/forumApi';
import { mapGetters } from 'vuex';
import TrendingTopicsContainer from '../components/TrendingTopicsContainer.vue';
import RelatedForumsContainer from '../components/RelatedForumsContainer.vue';
import RecentContentContainer from '../components/RecentContentContainer.vue';
import ForumStatsContainer from '../components/ForumStatsContainer.vue';
import NavigationLinks from '../components/NavigationLinks.vue';
import UserActivityWidget from '../components/UserActivityWidget.vue';

const axios = forumApi;

export default {
  name: 'ForumSection',
  components: {
    TrendingTopicsContainer,
    RelatedForumsContainer,
    RecentContentContainer,
    ForumStatsContainer,
    NavigationLinks,
    UserActivityWidget
  },
  data() {
    return {
      section: {
        id: null,
        title: '',
        description: ''
      },
      threads: [],
      loading: true,
      error: null,
      currentPage: 1,
      perPage: 20,
      totalThreads: 0,
    };
  },
  computed: {
    ...mapGetters(['_g_IsUserLogged', '_g_User']),
    isLoggedIn() {
      return this._g_IsUserLogged;
    },
    canWrite() {
      const user = this._g_User;
      const isAdmin = !!(user && user.is_admin_user);
      const isWriter = !!(user && user.is_forum_writer_user);
      return isAdmin || isWriter;
    },
    totalPages() {
      return Math.ceil(this.totalThreads / this.perPage);
    }
  },
  mounted() {
    this.fetchSectionData();
  },
  methods: {
    fetchSectionData() {
      const sectionSlug = this.$route.params.slug;
      this.loading = true;
      this.error = null; // Reset error state
      
      axios
        .get(`/api/forum-section/slug/${sectionSlug}`) // Use the new slug-based endpoint
        .then(response => {
          if (response.data && response.data.success) {
            const sectionData = response.data.section;
            
            this.section.id = sectionData.id.toString(); 
            this.section.title = sectionData.title;
            this.section.description = sectionData.description;
            // Optionally store the confirmed slug
            this.section.slug = sectionData.slug;

            // Now fetch threads for this section ID
            this.fetchThreads();
          } else {
            throw new Error(response.data.error || 'Section not found');
          }
        })
        .catch(err => {
          console.error('Error fetching section data by slug:', err);
          this.error = err.message || 'Error al cargar los datos de la sección';
          this.loading = false; // Stop loading on error
        });
    },
    fetchThreads() {
      this.loading = true;
      this.error = null;
      
      axios.get(`/api/forum-topics/${this.section.id}?page=${this.currentPage - 1}&limit=${this.perPage}`)
        .then(response => {
          if (response.data && response.data.success) {
            // Map API response to local thread structure
            this.threads = response.data.topics.map(topic => {
              return {
                id: topic.nid,
                title: topic.title,
                author: topic.author_name,
                authorId: topic.author_uid,
                created: topic.created,
                lastUpdate: topic.lastUpdate,
                commentCount: topic.commentCount
              };
            });
            
            // Update total count for pagination if available
            if (response.data.count !== undefined) {
              this.totalThreads = response.data.count;
            }
          } else {
            throw new Error('Invalid response format');
          }
        })
        .catch(err => {
          console.error('Error fetching threads:', err);
          this.error = 'Error al cargar los hilos.';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatDate(dateValue) {
      // Handle null or undefined
      if (dateValue === null || dateValue === undefined) {
        return 'Fecha desconocida';
      }
      
      // If it's a number (Unix timestamp)
      if (typeof dateValue === 'number') {
        // Convert Unix timestamp (seconds) to JavaScript Date (milliseconds)
        const date = new Date(dateValue * 1000);
        return date.toLocaleString();
      } 
      // If it's a string that looks like a number
      else if (typeof dateValue === 'string' && /^\d+$/.test(dateValue)) {
        // Convert string timestamp to number
        const timestamp = parseInt(dateValue, 10);
        const date = new Date(timestamp * 1000);
        return date.toLocaleString();
      }
      // If it's already a date string
      else if (dateValue) {
        try {
          const date = new Date(dateValue);
          
          // Check if date is valid
          if (isNaN(date.getTime())) {
            return 'Fecha inválida';
          }
          
          return date.toLocaleString();
        } catch (e) {
          return 'Error de fecha';
        }
      }
      
      return 'Fecha desconocida';
    },
    formatTimestamp(timestamp) {
      // Convert Unix timestamp to JavaScript Date
      const date = new Date(timestamp * 1000);
      return date.toLocaleString();
    },
    goToThread(thread) {
      this.$router.push({ name: 'ForumThread', params: { nid: thread.nid || thread.id } });
    },
    goToLastMessage(thread) {
      // Navegar al hilo con el parámetro de consulta para ir al último mensaje
      this.$router.push({ 
        name: 'ForumThread', 
        params: { nid: thread.id || thread.nid },
        query: { last: 'true' }
      });
    },
    fetchThreadCommentCount(thread) {
      return new Promise((resolve, reject) => {
        const threadId = thread.nid || thread.id;
        
        axios.get(`/forum/thread/${threadId}/comment-count`)
          .then(response => {
            if (response.data && response.data.count !== undefined) {
              resolve(response.data.count);
            } else {
              // If the API doesn't return a count, resolve with 0
              resolve(0);
            }
          })
          .catch(err => {
            console.error('Error fetching comment count:', err);
            // In case of error, assume there are comments and resolve with a default value
            resolve(1);
          });
      });
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchThreads();
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchThreads();
      }
    },
    createNewThread() {
      // Navigate to the new thread creation page with the current section slug as a query parameter
      this.$router.push({ 
        name: 'ForumNewThread',
        query: { sectionSlug: this.section.slug || this.$route.params.slug }
      });
    }
  }
};
</script>

<style scoped>
/* Contenedor general */
.forum-wrapper {
  background-color: var(--color-background);
  min-height: 100vh;
  padding: 40px 20px;
}

/* Layout de dos columnas */
.forum-layout {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  gap: 20px;
}

/* Estilos de la columna principal */
.main-column {
  flex: 3;
  color: var(--color-text-primary);
}

/* Cabecera de la sección */
.section-header {
  background-color: var(--color-surface);
  padding: 20px;
  border-radius: 15px 15px 0 0;
  margin-bottom: 0;
}

.section-header h1 {
  margin: 0 0 10px 0;
}

.section-description {
  margin: 0;
  color: var(--color-text-secondary);
}

/* Cabecera de hilos */
.threads-header {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.threads-header h2 {
  margin: 0;
}

.new-thread-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.new-thread-btn:hover {
  background-color: var(--color-highlight-dark, #1976d2);
}

.new-thread-btn i {
  font-size: 18px;
}

/* Contenedor de hilos */
.threads-container {
  background-color: var(--color-surface);
  border-radius: 0 0 15px 15px;
  overflow: hidden;
}

/* Cada hilo */
.thread-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid var(--color-outline);
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.thread-item:last-child {
  border-bottom: none;
}

.thread-item:hover {
  background-color: var(--color-active-surface);
}

.thread-main {
  flex: 1;
}

.thread-title {
  margin: 0 0 5px 0;
  font-weight: 600;
}

.thread-meta {
  display: flex;
  gap: 15px;
  color: var(--color-text-secondary);
}

.thread-author {
  display: flex;
  align-items: center;
  gap: 5px;
}

.thread-date {
  display: inline;
}

.thread-author i {
  font-size: 16px;
}

.thread-stats {
  display: flex;
  align-items: center;
  gap: 15px;
}

.thread-comments {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--color-text-secondary);
}

.thread-comments i {
  font-size: 18px;
}

.last-message-btn {
  background-color: var(--color-background);
  color: var(--color-text-primary);
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  text-decoration: none;
}

.last-message-btn:hover {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
}

/* Paginación */
.forum-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
  padding: 15px;
  background-color: var(--color-surface);
  border-radius: 15px;
}

.pagination-btn {
  background-color: var(--color-background);
  color: var(--color-text-primary);
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn:hover:not(:disabled) {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
}

/* Mensajes de carga y error */
.loading-message,
.error-message {
  padding: 20px;
  background-color: var(--color-surface);
  border-radius: 0 0 15px 15px;
  text-align: center;
  color: var(--color-text-primary);
}

.error-message {
  border-left: 4px solid #f44336;
}

/* Empty threads message */
.empty-threads-message {
  padding: 30px 20px;
  text-align: center;
  color: var(--color-text-primary);
  background-color: var(--color-surface);
  border-radius: 0 0 15px 15px;
}

/* Columna lateral */
.side-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.user-stats,
.forum-stats {
  background-color: var(--color-surface);
  padding: 20px;
  border-radius: 15px;
  color: var(--color-text-primary);
}

.user-stats h3,
.forum-stats h3 {
  margin-top: 0;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--color-outline);
}

/* Estadísticas de usuario */
.stats-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
}

.create-thread-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
  border: none;
  padding: 10px;
  border-radius: 4px;
  margin-top: 15px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 100%;
}

.create-thread-btn:hover {
  background-color: var(--color-highlight-dark, #1976d2);
}

.login-prompt {
  text-align: center;
}

.login-btn {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  margin-top: 10px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.login-btn:hover {
  background-color: var(--color-highlight-dark, #1976d2);
}

/* Responsividad */
@media (max-width: 768px) {
  .forum-layout {
    flex-direction: column;
  }
  
  .main-column,
  .side-column {
    flex: 1;
    width: 100%;
  }
  
  .side-column {
    margin-top: 20px;
  }
  
  .thread-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .thread-stats {
    width: 100%;
    justify-content: space-between;
    margin-top: 10px;
  }
}
</style> 